import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AsyncBoundary, Card, Drawer, Notice, useToast } from '@kollez/design-system';
import {
  useExam,
  useExamAssignments,
  useRevokeAssignment,
  useCreateAssignment,
  type ExamAssignmentRow,
} from '@/lib/queries';
import { formatIndianDate } from '@kollez/shared';

/**
 * /assess/exams/:id — one assessment's detail + assignments queue.
 *
 * Consumes GET /assessments/exams/:id + GET /assessments/exams/:id/assignments.
 * Ship 5g seeded AssessmentAssignment rows and both endpoints returned real
 * data, but the exams list had no click-through to see per-exam assignments.
 * This page shows the config summary at the top and a filter-toggled queue
 * of assignments below with revoke actions when the caller has authority.
 */

function assignmentState(a: ExamAssignmentRow): 'active' | 'revoked' | 'expired' {
  if (a.revokedAt) return 'revoked';
  if (a.expiresAt && new Date(a.expiresAt) < new Date()) return 'expired';
  return 'active';
}

export function AssessExamDetailPage() {
  const { id = '' } = useParams();
  const examQ = useExam(id);
  const [status, setStatus] = useState<'active' | 'revoked' | 'expired' | ''>('active');
  const assignmentsQ = useExamAssignments(id, { status, limit: 200 });
  const revoke = useRevokeAssignment();
  const create = useCreateAssignment();
  const toast = useToast();
  const [assignOpen, setAssignOpen] = useState(false);
  const [userIdsText, setUserIdsText] = useState('');
  const [filterProgram, setFilterProgram] = useState('');
  const [filterBatch, setFilterBatch] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [expiresAt, setExpiresAt] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    d.setHours(23, 59, 0, 0);
    return d.toISOString().slice(0, 16);
  });

  const exam = examQ.data?.assessment;
  const assignments = assignmentsQ.data?.assignments ?? [];
  const summary = assignmentsQ.data?.summary;

  async function doAssign() {
    const ids = userIdsText
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter((s) => /^[a-f0-9]{24}$/i.test(s));
    const hasFilter = Boolean(filterProgram || filterBatch || filterDepartment);
    if (ids.length === 0 && !hasFilter) {
      toast.show({ tone: 'warning', message: 'Add user IDs or a filter.' });
      return;
    }
    try {
      const iso = new Date(expiresAt).toISOString();
      const res = await create.mutateAsync({
        examId: id,
        userIds: ids.length > 0 ? ids : undefined,
        filter: !ids.length && hasFilter
          ? {
              program: filterProgram || undefined,
              batch: filterBatch ? Number(filterBatch) : undefined,
              department: filterDepartment || undefined,
            }
          : undefined,
        expiresAt: iso,
      });
      toast.show({ message: `Assigned to ${res.assignment.assignees.length} student${res.assignment.assignees.length === 1 ? '' : 's'}.` });
      setAssignOpen(false);
      setUserIdsText('');
      setFilterProgram('');
      setFilterBatch('');
      setFilterDepartment('');
    } catch (err) {
      toast.show({ tone: 'warning', message: err instanceof Error ? err.message : 'Assign failed.' });
    }
  }

  async function doRevoke(assignmentId: string) {
    const reason = window.prompt('Why are you revoking this assignment?')?.trim();
    if (!reason) return;
    try {
      await revoke.mutateAsync({ assignmentId, reason });
      toast.show({ message: 'Assignment revoked' });
    } catch (err) {
      toast.show({ tone: 'warning', message: err instanceof Error ? err.message : 'Revoke failed' });
    }
  }

  return (
    <AsyncBoundary
      kind="detail"
      loading={examQ.isLoading}
      error={examQ.error}
      empty={!examQ.isLoading && !exam}
      emptyTitle="Assessment not found"
      emptyHint="It may have been retired, or you may not have visibility into this tenant's assessments."
      onRetry={() => void examQ.refetch()}
    >
      {exam && (
        <div className="stack">
          <div className="page-header">
            <div className="ph-text">
              <p className="eyebrow">
                <Link to="/assess/exams">← All assessments</Link>
                {' · '}
                {exam.status ?? 'Assessment'}
              </p>
              <h1 className="h-page">{exam.name ?? exam.title ?? 'Assessment'}</h1>
              <p className="subhead">
                {exam.description ?? 'No description.'}
              </p>
            </div>
            <div className="ph-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={exam.status !== 'published'}
                title={exam.status === 'published' ? 'Assign this exam to a batch of students' : 'Only published exams can be assigned.'}
                onClick={() => setAssignOpen(true)}
              >
                Assign to students
              </button>
              <Link className="btn btn-outline" to={`/assess/exams/${id}/edit`}>Edit</Link>
              <Link className="btn btn-outline" to={`/assess/exams/${id}/results`}>Results</Link>
            </div>
          </div>

          {summary && (
            <div className="grid gap-3 sm:grid-cols-3">
              <button
                type="button"
                className={`card kpi text-left ${status === 'active' ? 'ring-2 ring-ink-accent' : ''}`}
                onClick={() => setStatus(status === 'active' ? '' : 'active')}
              >
                <span className="kpi-label">Active</span>
                <span className="kpi-value">{summary.active}</span>
              </button>
              <button
                type="button"
                className={`card kpi text-left ${status === 'expired' ? 'ring-2 ring-ink-accent' : ''}`}
                onClick={() => setStatus(status === 'expired' ? '' : 'expired')}
              >
                <span className="kpi-label">Expired</span>
                <span className="kpi-value">{summary.expired}</span>
              </button>
              <button
                type="button"
                className={`card kpi text-left ${status === 'revoked' ? 'ring-2 ring-ink-accent' : ''}`}
                onClick={() => setStatus(status === 'revoked' ? '' : 'revoked')}
              >
                <span className="kpi-label">Revoked</span>
                <span className="kpi-value">{summary.revoked}</span>
              </button>
            </div>
          )}

          <Card padding="none">
            <div className="card-head">
              <h2 className="h-section">
                Assignments {status && <span className="cell-sub">· {status}</span>}
              </h2>
              {status && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setStatus('')}>
                  Clear filter
                </button>
              )}
            </div>
            <AsyncBoundary
              kind="table"
              inCard
              loading={assignmentsQ.isLoading}
              error={assignmentsQ.error}
              empty={!assignmentsQ.isLoading && assignments.length === 0}
              emptyTitle={status ? `No ${status} assignments` : 'No assignments yet'}
              emptyHint={status
                ? 'Adjust the filter above or clear it to see every assignment.'
                : 'Post an assignment to a batch of students to start tracking attempts.'}
              onRetry={() => void assignmentsQ.refetch()}
            >
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Assignees</th>
                      <th>Created</th>
                      <th>Expires</th>
                      <th>State</th>
                      <th className="r">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignments.map((a) => {
                      const st = assignmentState(a);
                      const openedCount = a.assignees.filter((x) => x.firstOpenedAt).length;
                      return (
                        <tr key={a._id}>
                          <td>
                            <div className="cell-title">{a.assignees.length} student{a.assignees.length === 1 ? '' : 's'}</div>
                            <div className="cell-sub">{openedCount} opened</div>
                            {a.note && <div className="cell-sub truncate">{a.note}</div>}
                          </td>
                          <td className="cell-sub">{formatIndianDate(new Date(a.createdAt))}</td>
                          <td className="cell-sub">
                            {a.expiresAt ? formatIndianDate(new Date(a.expiresAt)) : '—'}
                          </td>
                          <td>
                            <span className={`badge ${st === 'active' ? 'badge-success' : st === 'revoked' ? 'badge-danger' : 'badge-warning'}`}>
                              {st}
                            </span>
                            {st === 'revoked' && a.revokedReason && (
                              <div className="cell-sub truncate">{a.revokedReason}</div>
                            )}
                          </td>
                          <td className="r">
                            {st === 'active' ? (
                              <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                disabled={revoke.isPending}
                                onClick={() => void doRevoke(a._id)}
                              >
                                Revoke
                              </button>
                            ) : (
                              <span className="cell-sub">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </AsyncBoundary>
          </Card>

          <Drawer open={assignOpen} onClose={() => setAssignOpen(false)} title="Assign this exam" size="lg">
            <div className="stack">
              <Notice tone="info">
                Post an assignment to a batch of students. Provide explicit user IDs OR a
                program/batch/department filter — one of the two is required. Students you
                assign are notified and appear in the assignments queue.
              </Notice>

              <label className="flex flex-col text-sm">
                <span className="cell-sub">Assignee user IDs (space, comma, or newline separated)</span>
                <textarea
                  className="input mt-1"
                  rows={3}
                  placeholder="e.g. 66f0…be21  66f0…de43"
                  value={userIdsText}
                  onChange={(e) => setUserIdsText(e.target.value)}
                />
              </label>

              <div className="cell-sub">Or resolve via filter (ignored when IDs are provided):</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <label className="flex flex-col text-sm">
                  <span className="cell-sub">Program</span>
                  <input
                    className="input mt-1"
                    placeholder="e.g. B.Tech"
                    value={filterProgram}
                    onChange={(e) => setFilterProgram(e.target.value)}
                  />
                </label>
                <label className="flex flex-col text-sm">
                  <span className="cell-sub">Batch year</span>
                  <input
                    className="input mt-1"
                    type="number"
                    placeholder="e.g. 2026"
                    value={filterBatch}
                    onChange={(e) => setFilterBatch(e.target.value)}
                  />
                </label>
                <label className="flex flex-col text-sm">
                  <span className="cell-sub">Department</span>
                  <input
                    className="input mt-1"
                    placeholder="e.g. CSE"
                    value={filterDepartment}
                    onChange={(e) => setFilterDepartment(e.target.value)}
                  />
                </label>
              </div>

              <label className="flex flex-col text-sm">
                <span className="cell-sub">Deadline</span>
                <input
                  className="input mt-1"
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
              </label>

              <div className="drawer-foot mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setAssignOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={create.isPending}
                  onClick={() => void doAssign()}
                >
                  {create.isPending ? 'Assigning…' : 'Assign'}
                </button>
              </div>
            </div>
          </Drawer>
        </div>
      )}
    </AsyncBoundary>
  );
}
